import type { AttributePolicyByKey } from "./attributeDefaultsTypes";
import {
  overlayAttributeKey,
  policyFlags,
  policyFromFlags,
  type AttributePolicyFlags,
} from "./attributeDefaultsTypes";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const pickBool = (
  value: Record<string, unknown>,
  field: keyof AttributePolicyFlags
): boolean | undefined =>
  typeof value[field] === "boolean" ? (value[field] as boolean) : undefined;

const parseStoredPolicy = (value: unknown) => {
  if (!isRecord(value)) {
    return null;
  }
  const flags: Partial<AttributePolicyFlags> = {};
  for (const field of [
    "hiddenFromPersonPanel",
    "readOnlyInPersonPanel",
    "defaultOnCards",
    "allowAddingNew",
  ] as const) {
    const next = pickBool(value, field);
    if (next !== undefined) {
      flags[field] = next;
    }
  }
  return policyFromFlags(flags);
};

/** Workspace config JSON: `{ byKey: { [attributeKey]: flags } }`. */
export const toPersistedAttributePolicies = (
  policies: AttributePolicyByKey
): { byKey: Record<string, AttributePolicyFlags> } => {
  const byKey: Record<string, AttributePolicyFlags> = {};
  for (const [key, policy] of Object.entries(policies)) {
    if (policy) {
      byKey[key] = policyFlags(policy);
    }
  }
  return { byKey };
};

export const parsePersistedAttributePolicies = (
  raw: unknown
): AttributePolicyByKey => {
  if (!isRecord(raw)) {
    return {};
  }
  const source = isRecord(raw["byKey"]) ? raw["byKey"] : raw;
  const policies: AttributePolicyByKey = {};
  for (const [key, value] of Object.entries(source)) {
    const branded = overlayAttributeKey(key);
    if (!branded) {
      continue;
    }
    const parsed = parseStoredPolicy(value);
    if (parsed) {
      policies[branded] = parsed;
    }
  }
  return policies;
};
